import { isDev } from '@myre/common-conf';
import { TextAlign } from '@myre/common-sheet';
import { TagType } from '@myre/common-tag';
import {
  ActionGroupComponent,
  type ActionGroup,
} from '@myre/ui-primitive-action-group';
import { ActionsDropdownComponent } from '@myre/ui-primitive-actions-dropdown';
import {
  ActionButtonComponent,
  BoxLoaderComponent,
  ButtonComponent,
  ButtonTheme,
  DateRangeComponent,
  LineLoaderComponent,
  MyuiComponent,
  TagComponent,
  TextWithIconsOrTagsComponent,
} from '@myre/ui-primitive-components';
import {
  MyreTableDataSource,
  PreventDoubleClickDirective,
  ScrollSlavesDirective,
  StickyDirective,
  StopPropagationDirective,
} from '@myre/ui-primitive-core';
import { EllipsisDirective } from '@myre/ui-primitive-ellipsis';
import { CheckboxComponent } from '@myre/ui-primitive-forms';
import { TooltipDirective } from '@myre/ui-primitive-tooltip-directive';

import { AsyncPipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  Output,
  ViewChild,
  ViewChildren,
  type QueryList,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  MAT_CHECKBOX_DEFAULT_OPTIONS,
  type MatCheckboxDefaultOptions,
} from '@angular/material/checkbox';
import { MatSort, MatSortModule, type Sort } from '@angular/material/sort';
import { castArray, compact } from 'lodash-es';
import {
  BehaviorSubject,
  combineLatest,
  of,
  Subject,
  switchMap,
  type Observable,
  type Subscription,
} from 'rxjs';
import { debounceTime, filter, map } from 'rxjs/operators';

import { CellType, type Cell, type StringCell } from '../cells.interfaces';
import {
  MultiTableType,
  type Column,
  type MultiTableActionsFooter,
  type Row,
  type RowCheckboxState,
  type Table,
  type TrustedMultiTableActionsFooter,
} from '../table.interfaces';

import { FormCellComponent } from './cells/form-cell/form-cell.component';
import { EmptyTranslatePipe } from './pipes/empty-translate.pipe';
import { ShouldStopEventPipe } from './pipes/should-stop-event.pipe';
import { TotalElementsTranslatePipe } from './pipes/total-element.pipe';
import { RowSelectionService } from './services/row-selection.service';

/** A disabled row is excluded: its checkbox is unchecked and locked. */
function lockDisabledRow<Key extends string>(row: Row<Key>): Row<Key> {
  if (!row.isDisabled) {
    return row;
  }

  return {
    ...row,
    checkboxState: { ...row.checkboxState, checked: false, readonly: true },
  };
}

function isStringCell(cell: Cell): cell is StringCell {
  return typeof (cell as StringCell).value === 'string';
}

/**
 * This component Display one or more tables grouped under the same column system.
 * The loading state is managed by the component itself if @Input tables is nullable.
 */
@Component({
  selector: 'myre-multi-table',
  templateUrl: './multi-table.component.html',
  styleUrls: [
    './multi-table.component.scss',
    './themes/minimal-theme.scss',
    './themes/integral-theme.scss',
    './themes/compact-theme.scss',
    './themes/list-theme.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ActionButtonComponent,
    ActionGroupComponent,
    ActionsDropdownComponent,
    AsyncPipe,
    BoxLoaderComponent,
    ButtonComponent,
    CheckboxComponent,
    DateRangeComponent,
    EllipsisDirective,
    EmptyTranslatePipe,
    FormCellComponent,
    FormsModule,
    LineLoaderComponent,
    MatSortModule,
    NgClass,
    NgStyle,
    NgTemplateOutlet,
    PreventDoubleClickDirective,
    ScrollSlavesDirective,
    ShouldStopEventPipe,
    StickyDirective,
    StopPropagationDirective,
    TagComponent,
    TextWithIconsOrTagsComponent,
    TooltipDirective,
    TotalElementsTranslatePipe,
  ],
  providers: [
    RowSelectionService,
    {
      provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
      useValue: { clickAction: 'noop' } as MatCheckboxDefaultOptions,
    },
  ],
})
export class MultiTableComponent extends MyuiComponent {
  public override mSelector = 'myre-multi-table';

  protected readonly _bannerTemplate = computed(() =>
    this._getTemplate('banner'),
  );

  @ViewChild('headerRef')
  protected set headerTableRef(headerTable: ElementRef<HTMLElement>) {
    if (!headerTable) {
      return;
    }

    this._tableHeaderElement$.next(headerTable.nativeElement);
  }

  @ViewChildren('bodyRef')
  protected set bodyTablesRef(
    bodyTablesContainer: QueryList<ElementRef<HTMLElement>>,
  ) {
    // We only retrieve the reference of the slaves once
    if (this._bodyTablesRefSubscription || !bodyTablesContainer) {
      return;
    }

    // set the default table body elements
    this._emitTableBodyElements(bodyTablesContainer);

    // listen the potential future table body elements changes
    this._bodyTablesRefSubscription = bodyTablesContainer.changes
      .pipe(debounceTime(100), takeUntilDestroyed(this._destroyRef))
      .subscribe((queryList: QueryList<ElementRef>) =>
        this._emitTableBodyElements(queryList),
      );
  }

  private _sortSubscription?: Subscription;

  @ViewChild(MatSort)
  protected set _sort(sort: MatSort) {
    if (!sort || sort === this._dataSource.sort) {
      return;
    }

    this._dataSource.sort = sort;
    this._sortSubscription?.unsubscribe();
    this._sortSubscription = sort.sortChange
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(event => this.sortChange.emit(event));
  }

  /**
   * Managing the column headers of all displayed tables as well as the options applied to each of them.
   */
  @Input({ required: true })
  public set columns(columns: readonly Column<string>[]) {
    if (!columns) {
      return;
    }

    const defaultColumnWidth = this.widthInPixel ? 'auto' : '1';

    this._columns = columns.map(column => ({
      ...column,
      canSort: column.canSort ?? false,
      width: column.width ?? defaultColumnWidth,
      align: column.align || TextAlign.Left,
    }));
  }

  /**
   * The different tables to display, containing rows and subheaders.
   * If only one table is passed, you can directly pass an object of type Table without using an array.
   */
  @Input({ required: true })
  public set tables(data: readonly Table<string>[] | Table<string>) {
    if (!data) {
      // Loading state
      this._dataSource.data = null;

      return;
    }

    const tables = castArray(data);

    this._multiTables = tables.length > 1;

    this._dataSource.data = tables.map(table => ({
      ...table,
      isOpen: table.isOpen ?? true,
      rows: table.rows
        ? table.rows.map(row => lockDisabledRow(row))
        : table.rows,
    }));

    this._resetRowSelectionService();
  }

  /**
   * The callback function to call when a column is sorted.
   * Overrides the general default sorting function.
   */
  @Input()
  public sortCallback: (
    data: Table<string>[],
    sort: MatSort,
  ) => Table<string>[];

  /**
   * Whether data are filtered or not ?
   * If the value is truthy and the length of the data is 0, the component will display a message indicating that the filtered data is empty.
   * If the value is a string, the message displayed when the data is empty will be the value of the input.
   *
   * If the value is falsy, the component will display a message indicating that the data is empty.
   */
  @Input()
  public filterStatus: boolean | string;

  /**
   * The theme to use for the component, mainly affecting spacing and borders.
   */
  @Input()
  public theme: 'minimal' | 'compact' | 'integral' | 'list' = 'integral';

  /**
   * Display every other row in a different color.
   */
  @Input()
  public stripedRow = true;

  /**
   * Display a background color on the hovered row.
   */
  @Input()
  public hover = true;

  /**
   * Display the index on the first column of each non-header row.
   */
  @Input()
  public showIndex = false;

  /**
   * Specifies whether the column width is in percentage or pixels.
   * If horizontal scrolling is desired, it's mandatory to use pixels.
   */
  @Input()
  public widthInPixel = false;

  @Input()
  public stickyHeader = true;

  /**
   * With `widthInPixel`, keeps the first column in place while the others scroll horizontally.
   */
  public readonly stickyFirstColumn = input(false, {
    transform: booleanAttribute,
  });

  public readonly headerActionButton = input<ActionGroup | null>(null);

  /**
   * The footer to display at the bottom of the table.
   */
  @Input()
  public set footer(footer: MultiTableActionsFooter) {
    this._footer = {
      ...footer,
      leftButtons: compact(castArray(footer.leftButtons)),
    };
  }

  /**
   * Whether each row is selectable or not.
   */
  @Input()
  public set canSelectRows(canSelectRows: boolean) {
    this._canSelectRows$.next(canSelectRows);

    this._resetRowSelectionService();
  }

  /**
   * Used as a notifier to select or deselect all rows.
   */
  @Input()
  public set switchSelectAllNotifier$(notifier$: Observable<void>) {
    // We only subscribe once
    if (this._selectAllSubscription) {
      return;
    }

    this._selectAllSubscription = notifier$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._rowSelectionService.selectAll(this._getRows());
      });
  }

  /**
   * Emits the checkbox state of each row when the selection changes.
   */
  @Output()
  public readonly rowSelectionChange = new EventEmitter<
    Map<string | number, RowCheckboxState>
  >();

  /**
   * Emits when all rows are selected or deselected.
   */
  @Output()
  public readonly selectAllChange = new EventEmitter<boolean>();

  /**
   * Emits when the user changes the column sort (header click). Consumers that
   * paginate externally should listen to this and sort their source data
   * upstream, since the default sort only sees the current page slice.
   */
  @Output()
  public readonly sortChange = new EventEmitter<Sort>();

  protected readonly _rowSelectionService = inject(RowSelectionService);

  protected readonly _TagType = TagType;
  protected readonly _CellType = CellType;
  protected readonly _MultiTableType = MultiTableType;
  protected readonly _ButtonTheme = ButtonTheme;

  /**
   * @ignore
   */
  protected _columns: Column<string>[];

  /**
   * @ignore
   */
  protected _multiTables: boolean;

  /**
   * @ignore
   */
  protected _footer: TrustedMultiTableActionsFooter;

  /**
   * @ignore
   */
  protected _dataToDisplay$: Observable<readonly Table<string>[]>;

  /**
   * @ignore
   */
  protected _tableScrollableElement$: Observable<HTMLElement[]>;

  /**
   * @ignore
   */
  protected _canSelectRows$ = new BehaviorSubject<boolean>(false);

  /**
   * @ignore
   */
  private _dataSource: MyreTableDataSource<Table<string>>;

  /**
   * @ignore
   */
  private readonly _tableHeaderElement$ = new Subject<HTMLElement>();

  /**
   * @ignore
   */
  private readonly _tableBodyElements$ = new Subject<HTMLElement[]>();

  /**
   * @ignore
   */
  private _bodyTablesRefSubscription: Subscription | null = null;
  private _selectAllSubscription: Subscription | null = null;

  protected readonly _selectedItems = toSignal(
    this._rowSelectionService.rowIdCheckboxStateMap$.pipe(
      map(rowIdCheckboxStateMap =>
        Array.from(rowIdCheckboxStateMap.entries())
          .filter(([_, rowCheckboxState]) => rowCheckboxState.checked)
          .map(([rowId]) => rowId),
      ),
    ),
  );

  protected readonly _areItemsSelected = computed(() => {
    return this._selectedItems().length && this.headerActionButton();
  });

  protected readonly _isBannerVisible = computed(() => {
    return this._areItemsSelected() || this._bannerTemplate();
  });

  constructor() {
    super();

    this._manageDataToDisplay();
    this._combineTableElementsForScrolling();

    combineLatest([
      this._canSelectRows$,
      this._rowSelectionService.rowIdCheckboxStateMap$,
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([_, rowIdCheckboxStateMap]) => {
        this.rowSelectionChange.emit(rowIdCheckboxStateMap);
      });

    this._rowSelectionService.allSelected$
      .pipe(takeUntilDestroyed())
      .subscribe(allSelected => this.selectAllChange.emit(allSelected));
  }

  public selectRows(ids: Array<string | number>): void {
    this._rowSelectionService.selectAll(
      this._getRows().filter(row => ids.includes(row.id)),
    );
  }

  /**
   * @ignore
   */
  protected _toggleVisibility(table: Table<string>): void {
    if (table.isCollapseFixed) {
      return;
    }

    table.isOpen = !table.isOpen;
  }

  protected _getRows(): Row<string>[] {
    return compact(this._dataSource.data?.flatMap(table => table.rows));
  }

  protected _selectOne(row: Row<string>, event: MouseEvent): void {
    event.stopPropagation();

    if (row.isDisabled) {
      return;
    }

    const strictValue =
      typeof row.onCheckboxClick === 'function'
        ? row.onCheckboxClick(event)
        : null;

    this._rowSelectionService.selectOne(
      row,
      typeof strictValue === 'boolean' ? strictValue : null,
    );
  }

  /**
   * @ignore
   */
  private _manageDataToDisplay(): void {
    this._dataSource = new MyreTableDataSource<Table<string>>(null);
    this._dataSource.sortData = (
      data: Table<string>[],
      sort: MatSort,
    ): Table<string>[] => {
      if (this.sortCallback && typeof this.sortCallback === 'function') {
        return this.sortCallback(data, sort);
      }

      return this._defaultSort(data, sort);
    };

    this._dataToDisplay$ = combineLatest([
      this._dataSource.connect().asObservable(),
      this._canSelectRows$,
    ]).pipe(
      switchMap(([data, canSelectRows]) =>
        canSelectRows
          ? this._rowSelectionService.rowIdCheckboxStateMap$.pipe(
              map(
                rowIdCheckboxStateMap =>
                  [data, canSelectRows, rowIdCheckboxStateMap] as const,
              ),
            )
          : of([data, canSelectRows, null] as const),
      ),
      map(([data, canSelectRows, rowIdCheckboxStateMap]) => {
        if (!canSelectRows || !data) {
          return data;
        }

        // For each row, we update the checkbox state considering rowSelectionService _rowIdCheckboxStateMap
        return data.map(table => ({
          ...table,
          rows: table.rows?.map(row => ({
            ...row,
            // The tooltip is display only: it follows the input, not the selection
            checkboxState: {
              ...rowIdCheckboxStateMap.get(row.id),
              tooltip: row.checkboxState?.tooltip,
            },
          })),
        }));
      }),
    );
  }

  /**
   * Combines the table header and body tables to create an Observable that manages scrollable items.
   * This combination allows for simultaneous manipulation of the scrolling elements in the table's header and body.
   */
  private _combineTableElementsForScrolling(): void {
    this._tableScrollableElement$ = combineLatest([
      this._tableHeaderElement$,
      this._tableBodyElements$,
    ]).pipe(
      filter(
        ([tableHeader, tablesBody]) => !!tableHeader && !!tablesBody?.length,
      ),
      map(([tableHeader, tablesBody]) => [tableHeader, ...tablesBody]),
    );
  }

  /**
   * @ignore
   */
  private _defaultSort(data: Table<string>[], sort: MatSort): Table<string>[] {
    if (sort.direction === '') {
      return data;
    }

    const [firstKey] = sort.sortables.keys();
    const key = sort.active || firstKey;

    const ascOperator = sort.direction === 'asc' ? -1 : 1;

    return data.map(table => {
      if (!table.rows?.length) {
        return table;
      }

      return {
        ...table,
        rows: table.rows.sort((a, b) => {
          const cellA = a.cells[key];
          const cellB = b.cells[key];

          if (!isStringCell(cellA) || !isStringCell(cellB)) {
            throw new Error(
              'The sorting function only works with string cells.',
            );
          }

          return ascOperator * cellA.value.localeCompare(cellB.value);
        }),
      };
    });
  }

  /**
   * @ignore
   */
  private _emitTableBodyElements(
    bodyTables: QueryList<ElementRef<HTMLElement>>,
  ): void {
    this._tableBodyElements$.next(bodyTables.map(ref => ref.nativeElement));
  }

  private _resetRowSelectionService(): void {
    if (!this._canSelectRows$.getValue()) {
      return;
    }

    const rows = this._getRows();

    // The initialization of row selections should only be done when the data displayed in the table changes.
    // If the data IDs are identical, it means the table has evolved without the selection data changing.
    // We do not want to reinitialize the rowSelectionService to avoid infinite looping.
    const rowsChanged = this._rowsChanged(rows);

    if (!rowsChanged) {
      // A row can still turn disabled or enabled, its stored state must follow.
      this._rowSelectionService.syncDisabledRows(rows);

      return;
    }

    // Ensure that rows contains required checkboxState property
    for (const row of rows) {
      if (!row.checkboxState) {
        if (isDev()) {
          // eslint-disable-next-line no-console
          console.warn(
            'Row does not have a checkboxState property, setting default value',
            row,
          );
        }

        row.checkboxState = {
          checked: false,
          readonly: false,
        };
      }
    }

    this._rowSelectionService.reset(rows);
  }

  /**
   * Check if all ids of the rows and the current rowIdCheckboxState are identical.
   */
  private _rowsChanged(rows: readonly Row<string>[]): boolean {
    const rowMap = this._rowSelectionService.getRowIdCheckboxStateMapSnapshot();

    const rowIds = new Set(rows.map(row => row.id));
    const mapKeys = new Set(rowMap.keys());

    return (
      rowIds.size !== mapKeys.size || ![...rowIds].every(id => mapKeys.has(id))
    );
  }
}
